<template>
  <div class="condition-content">
    <!-- 左侧菜单栏 -->
    <aside class="sidebar">
      <div class="sidebar-header">
        <h3 class="sidebar-title">报考条件</h3>
      </div>
      <nav class="sidebar-nav">
        <button
          v-for="menu in menuItems"
          :key="menu.id"
          :class="['nav-item', { active: activeMenu === menu.id }]"
          @click="activeMenu = menu.id"
        >
          <i :class="menu.icon" class="nav-icon"></i>
          <span class="nav-text">{{ menu.label }}</span>
        </button>
      </nav>
    </aside>

    <!-- 右侧内容区 -->
    <main class="main-content">
      <!-- 基本条件 -->
      <div v-show="activeMenu === 'basic'" class="content-section">
        <div class="section-header">
          <h2 class="section-title">基本条件</h2>
        </div>
        <div class="basic-grid">
          <div
            v-for="(card, index) in basicCards"
            :key="index"
            class="condition-card"
          >
            <div class="icon-box" v-if="card.icon">
              <i :class="card.icon"></i>
            </div>
            <h3 class="card-title">{{ card.title }}</h3>
            <p class="card-desc">{{ card.description }}</p>
          </div>
        </div>
      </div>

      <!-- 身体条件 -->
      <div v-show="activeMenu === 'physical'" class="content-section">
        <div class="section-header">
          <h2 class="section-title">身体条件</h2>
          <p class="section-desc">
            军队院校对考生身体条件有严格要求，包括身高、体重、视力等多个方面
          </p>
        </div>

        <div class="tab-container">
          <div class="tab-header">
            <button
              v-for="tab in tabs"
              :key="tab.name"
              :class="['tab-button', { active: activeTab === tab.name }]"
              @click="activeTab = tab.name"
            >
              {{ tab.label }}
            </button>
          </div>
          <div class="tab-content">
            <div v-show="activeTab === 'height-weight'" class="tab-pane">
              <div class="content-box">
                <h3 class="sub-title">身高要求</h3>
                <ul class="custom-list">
                  <li>男性身高不低于1.62米，女性身高不低于1.60米</li>
                </ul>
              </div>
            </div>

            <div v-show="activeTab === 'vision'" class="tab-pane">
              <div class="content-box">
                <h3 class="sub-title">裸眼视力</h3>
                <ul class="custom-list">
                  <li>裸眼视力须达到4.5及以上。</li>
                  <li>矫正视力须达到4.8及以上，且矫正度数不得超过600度。</li>
                  <li>
                    如进行屈光不正矫正手术（含激光手术和非激光手术），手术时间距报考当年军队院校招收学员体格检查须满6个月以上（即须在体格检查前至少6个月完成手术）。
                  </li>
                </ul>
              </div>
            </div>
            <div v-show="activeTab === 'other'" class="tab-pane">
              <div class="empty-state">
                <i class="fa fa-refresh fa-spin"></i>
                <p>正在更新中......</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 政治条件 -->
      <div v-show="activeMenu === 'political'" class="content-section">
        <div class="section-header">
          <h2 class="section-title">政治条件</h2>
          <p class="section-desc">
            按照《军队征集和招录人员政治考核规定》
          </p>
        </div>
        <div class="political-grid">
          <div class="condition-card">
            <h3 class="card-title">考核内容</h3>
            <ul class="custom-list">
              <li>本人对党的路线、方针、政策的态度；政治历史和在重大政治斗争中的表现</li>
              <li>直系亲属和对本人有重大影响的旁系亲属的政治情况</li>
              <li>社会关系情况</li>
              <li>是否有犯罪记录或其他不良行为</li>
              <li>是否参加过非法组织或活动</li>
            </ul>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script setup>
import { ref } from "vue";

const activeMenu = ref("basic");
const menuItems = [
  { id: "basic", label: "基本条件", icon: "fa fa-user" },
  { id: "physical", label: "身体条件", icon: "fa fa-heartbeat" },
  { id: "political", label: "政治条件", icon: "fa fa-shield" },
];

const activeTab = ref("height-weight");
const tabs = [
  { name: "height-weight", label: "身高体重" },
  { name: "vision", label: "视力要求" },
  { name: "other", label: "其他条件" },
];

const basicCards = [
  {
    icon: "fa fa-calendar text-primary-icon text-2xl",
    title: "年龄要求",
    description: "考生年龄不低于16周岁、不超过20周岁（截止至报考当年8月31日）",
  },
  {
    icon: "fa fa-graduation-cap text-primary-icon text-2xl",
    title: "学历要求",
    description: "考生须具有普通高级中学应届或往届毕业学历。中等专业学校、职业高中等非普通高中学历毕业生不符合报考条件。",
  },
  {
    icon: "fa fa-user text-primary-icon text-2xl",
    title: "性别要求",
    description: "军队院校招生计划分为男生计划和女生计划。",
  },
  {
    icon: "fa fa-heartbeat text-primary-icon text-2xl",
    title: "健康状况",
    description: "考生应身体健康，符合《军队选拔军官和文职人员体检标准》，其中包括身高、体重、视力等多方面的要求。",
  },
  {
    icon: "fa fa-users text-primary-icon text-2xl",
    title: "政治条件",
    description: "考生政治条件必须符合军队院校招生要求，必须热爱中国共产党，热爱社会主义祖国，热爱人民，热爱军队。",
  },
  {
    icon: "fa fa-book text-primary-icon text-2xl",
    title: "成绩要求",
    description: "报考22所军队院校的考生，须参加全国普通高等学校招生统一考试，且其高考成绩须不低于考生所在省份的本科第一批次录取控制分数线。",
  },
];
</script>

<style scoped>
/* 主容器 - 左右布局 */
.condition-content {
  display: flex;
  min-height: calc(100vh - 60px);
  background: #f8fafc;
}

/* 左侧菜单栏 */
.sidebar {
  width: 280px;
  background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
  border-right: 1px solid #e2e8f0;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  box-shadow: 8px 0 32px rgba(0, 0, 0, 0.04);
}

.sidebar-header {
  padding: 20px 24px;
  background: white;
  border-bottom: 2px solid #e2e8f0;
}

.sidebar-title {
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 12px;
}

.sidebar-title::before {
  content: '';
  width: 3px;
  height: 20px;
  background: linear-gradient(180deg, #3b82f6, #10b981);
  border-radius: 2px;
}

.sidebar-nav {
  flex: 1;
  padding: 20px 12px;
}

.nav-item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 20px;
  margin-bottom: 8px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  color: #64748b;
  cursor: pointer;
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 15px;
  font-weight: 500;
  text-align: left;
}

.nav-item:hover {
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  border-color: #3b82f6;
  transform: translateX(4px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
  color: #1d4ed8;
}

.nav-item.active {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  border-color: #1d4ed8;
  color: white;
  box-shadow: 0 6px 20px rgba(59, 130, 246, 0.35);
  transform: translateX(4px);
}

.nav-icon {
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.nav-item.active .nav-icon {
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
}

/* 右侧内容区 */
.main-content {
  flex: 1;
  padding: 24px;
  overflow-y: auto;
}

.content-section {
  background: white;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
}

/* 区域头部 */
.section-header {
  text-align: center;
  margin-bottom: 28px;
  position: relative;
}

.section-header::after {
  content: '';
  width: 60px;
  height: 4px;
  background: linear-gradient(90deg, #3b82f6, #1d4ed8);
  border-radius: 2px;
  position: absolute;
  bottom: -8px;
  left: 50%;
  transform: translateX(-50%);
}

/* 区域标题 */
.section-title {
  font-size: 28px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 16px;
  letter-spacing: 1px;
}

@media (min-width: 768px) {
  .section-title {
    font-size: 32px;
  }
}

/* 区域描述 */
.section-desc {
  color: #64748b;
  max-width: 768px;
  margin-left: auto;
  margin-right: auto;
  font-size: 15px;
  line-height: 1.6;
}

/* 基本条件网格布局 */
.basic-grid {
  display: grid;
  grid-template-columns: repeat(1, minmax(0, 1fr));
  gap: 20px;
}

@media (min-width: 768px) {
  .basic-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 24px;
  }
}

@media (min-width: 1024px) {
  .basic-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 28px;
  }
}

/* 政治条件网格布局 */
.political-grid {
  display: grid;
  grid-template-columns: repeat(1, minmax(0, 1fr));
  gap: 24px;
}

@media (min-width: 1024px) {
  .political-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 28px;
  }
}

/* 条件卡片 */
.condition-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.06);
  padding: 28px;
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid #e2e8f0;
  position: relative;
  overflow: hidden;
}

.condition-card::before {
  content: '';
  width: 4px;
  height: 100%;
  background: linear-gradient(180deg, #3b82f6 0%, #1d4ed8 100%);
  position: absolute;
  left: 0;
  top: 0;
  opacity: 0;
  transition: opacity 0.3s;
}

.condition-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 20px 40px rgba(59, 130, 246, 0.12), 0 4px 16px rgba(0, 0, 0, 0.08);
  border-color: #3b82f6;
}

.condition-card:hover::before {
  opacity: 1;
}

/* 图标盒子 */
.icon-box {
  width: 64px;
  height: 64px;
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
  transition: all 0.3s;
}

.condition-card:hover .icon-box {
  transform: scale(1.1);
  box-shadow: 0 8px 20px rgba(59, 130, 246, 0.25);
}

/* 卡片标题 */
.card-title {
  font-size: 19px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
}

/* 卡片描述 */
.card-desc {
  color: #64748b;
  font-size: 14px;
  line-height: 1.6;
}

/* 自定义列表 */
.custom-list {
  list-style: none;
  padding-left: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.custom-list li {
  display: flex;
  align-items: flex-start;
  padding: 12px 16px 12px 32px;
  background: #f8fafc;
  border-radius: 10px;
  font-size: 14px;
  color: #475569;
  position: relative;
}

.custom-list li::before {
  content: '';
  width: 6px;
  height: 6px;
  background: #3b82f6;
  border-radius: 50%;
  position: absolute;
  left: 18px;
  top: 50%;
  transform: translateY(-50%);
}

/* Tab 容器 */
.tab-container {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
  border: 1px solid #e2e8f0;
}

/* Tab 头部 */
.tab-header {
  display: flex;
  background: linear-gradient(180deg, #fafbfc 0%, #f1f5f9 100%);
  border-bottom: 1px solid #e2e8f0;
}

/* Tab 按钮 */
.tab-button {
  flex: 1;
  padding: 18px 24px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.35s;
  background: transparent;
  border: none;
  color: #64748b;
  position: relative;
}

.tab-button:hover {
  color: #3b82f6;
  background: rgba(59, 130, 246, 0.05);
}

.tab-button.active {
  color: #3b82f6;
}

.tab-button.active::after {
  content: '';
  width: 100%;
  height: 3px;
  background: linear-gradient(90deg, #3b82f6, #1d4ed8);
  border-radius: 2px;
  position: absolute;
  bottom: 0;
  left: 0;
}

/* Tab 内容 */
.tab-content {
  padding: 30px;
}

/* 内容盒子 */
.content-box {
  margin-bottom: 24px;
}

.content-box:last-child {
  margin-bottom: 0;
}

/* 子标题 */
.sub-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 16px;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 10px;
}

.sub-title::before {
  content: '';
  width: 3px;
  height: 20px;
  background: linear-gradient(180deg, #3b82f6, #1d4ed8);
  border-radius: 2px;
}

/* 图标颜色 */
.text-primary-icon {
  color: #3b82f6;
}

/* 图标大小 */
.text-2xl {
  font-size: 28px;
}

/* 空状态 */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #94a3b8;
}

.empty-state i {
  font-size: 48px;
  margin-bottom: 16px;
  color: #cbd5e1;
}

.empty-state p {
  font-size: 15px;
  margin: 0;
}

/* 响应式布局 */
@media (max-width: 768px) {
  .condition-content {
    flex-direction: column;
  }
  
  .sidebar {
    width: 100%;
    min-height: auto;
  }
  
  .sidebar-nav {
    display: flex;
    flex-wrap: wrap;
    padding: 8px;
  }
  
  .nav-item {
    flex: 1;
    min-width: calc(33.333% - 8px);
    justify-content: center;
    text-align: center;
    border-radius: 8px;
    margin: 4px;
    border-left: none;
  }
  
  .nav-item.active {
    border-left: none;
    border-radius: 8px;
  }
  
  .main-content {
    padding: 16px;
  }
  
  .content-section {
    padding: 20px;
  }
  
  .basic-grid {
    grid-template-columns: 1fr;
  }
}
</style>
